import { useState } from 'react';
import type { DateLength } from '@/api/types';
import { SpeechBubble } from '@/components/fun';
import { Icon } from '@/components/ui/Icon';
import { cn } from '@/lib/cn';
import { READY_MESSAGES, TONES, fillMessage, withLink, type Tone } from './messages';

export interface ReadyMessage {
  tone: Tone;
  setTone: (t: Tone) => void;
  /** The picked line (without the link). */
  line: string;
  /** What gets sent: the line, then the link. */
  text: string;
  shuffle: () => void;
}

/** v9 §A2 / SPEC_V10: one pre-picked warm line; tones as chips, shuffle for another. */
export function useReadyMessage(url: string, length: DateLength = 30): ReadyMessage {
  const [tone, setToneRaw] = useState<Tone>('sweet');
  const [n, setN] = useState(0);
  const lines = READY_MESSAGES[tone].map((l) => fillMessage(l, length));
  const line = lines[n % lines.length]!;
  return {
    tone,
    setTone: (t) => {
      setToneRaw(t);
      setN(0);
    },
    line,
    text: withLink(line, url),
    shuffle: () => setN((x) => x + 1),
  };
}

/**
 * The message picker in both share surfaces (invite share sheet, personal
 * link): tone chips, the line as a chat bubble, and shuffle for another one. The
 * caller's WhatsApp / copy / share buttons send `msg.text`.
 */
export function ReadyMessages({ msg, testId = 'ready-messages' }: { msg: ReadyMessage; testId?: string }) {
  return (
    <section className="space-y-2.5" aria-label="Your message" data-testid={testId}>
      <div role="radiogroup" aria-label="Tone" className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {TONES.map((t) => (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={msg.tone === t.id}
            onClick={() => msg.setTone(t.id)}
            className={cn(
              'h-9 shrink-0 whitespace-nowrap rounded-full px-4 text-[13px] font-medium transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg',
              msg.tone === t.id ? 'bg-fg text-ground' : 'bg-raised text-fg hover:bg-line',
            )}
            data-testid={`ready-tone-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <SpeechBubble side="left" tone="paper" bubbleKey={msg.line} className="min-w-0 flex-1" testId="ready-message">
          <span className="font-medium" data-tone={msg.tone}>
            {msg.line}
          </span>
        </SpeechBubble>
        <button
          type="button"
          onClick={msg.shuffle}
          aria-label="Another line"
          title="Another line"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-raised text-fg transition-[background-color,transform] duration-150 ease-out hover:bg-line active:scale-[0.96] focus-visible:outline focus-visible:outline-2 focus-visible:outline-fg"
          data-testid="ready-shuffle"
        >
          <Icon name="flip" size={20} />
        </button>
      </div>
    </section>
  );
}
